import React, { useState, useEffect, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import CircularProgress from "@mui/material/CircularProgress";
import Checkbox from "@mui/material/Checkbox";
import Autocomplete from "@mui/material/Autocomplete";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import DeleteIcon from "@mui/icons-material/Delete";
import GTranslateIcon from "@mui/icons-material/GTranslate";
import Collapse from "@mui/material/Collapse";
import axios from "axios";
import Swal from "sweetalert2";
import { getImage } from "../../../demo/utilities/indexedDB";
import { getAuthCredentials } from "../../../utils/auth";
import { translateText } from "../../../component/TranslateText";
import AddMenuCategoryDialog from "./AddMenuCategoryDialog";
import HandleCropMenuImage from "../Functions/HandeCropMenuImage";
import SubRecipeDialog from "./SubRecipeDialog";

/* ----------------------- ID utils (robust) ----------------------- */
const HEX24 = /^[a-fA-F0-9]{24}$/;
const isValidHexId = (s) => typeof s === "string" && HEX24.test(s);

const normalizeId = (id) => {
  if (!id) return "";
  if (typeof id === "string") {
    if (id === "[object Object]") return "";
    return id;
  }
  if (typeof id === "object") {
    if (id.$oid) return String(id.$oid);
    if (id._id) return normalizeId(id._id);
    if (typeof id.toHexString === "function") return id.toHexString();
    if (typeof id.toString === "function") {
      const s = id.toString();
      return isValidHexId(s) ? s : "";
    }
    return "";
  }
  try {
    const s = String(id);
    return isValidHexId(s) ? s : "";
  } catch {
    return "";
  }
};
/* ---------------------------------------------------------------- */

const qtyKeyOf = (row) => row.uiKey || row.inventoryId;

const DEFAULT_TX_TYPES = [
  { type: "dine-in", enabled: true },
  { type: "take-out", enabled: true },
  { type: "delivery", enabled: true },
  { type: "complimentary", enabled: false },
];

const EditMenuItemDialog = ({
  open,
  onClose,
  item,
  ingredients,
  menuCategories,
  setMenuCategories,
  menuItems,
  fetchMenuCategories,
  fetchMenuData,
}) => {
  const [name, setName] = useState("");
  const [translations, setTranslations] = useState({
    en: "",
    tl: "",
    zh: "",
    th: "",
  });
  const [showTranslations, setShowTranslations] = useState(false);
  const [loadingTranslation, setLoadingTranslation] = useState({
    en: false,
    tl: false,
    zh: false,
    th: false,
  });

  const [category, setCategory] = useState("");
  const [tags, setTags] = useState("");
  const [isAddingCategory, setIsAddingCategory] = useState(false);

  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);

  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [ingredientQuantities, setIngredientQuantities] = useState({});
  const [price, setPrice] = useState("");
  const [code, setCode] = useState("");
  const [uniqueName, setUniqueName] = useState("");
  const [description, setDescription] = useState("");
  const [addons, setAddons] = useState([]);
  const [processTime, setProcessTime] = useState(0);
  const [menuPax, setMenuPax] = useState(1);
  const [nameError, setNameError] = useState("");
  const [transactType, setTransactType] = useState(DEFAULT_TX_TYPES);

  // Sub-recipe UI
  const [subRecipeOpen, setSubRecipeOpen] = useState(false);
  const [showSubRecipes, setShowSubRecipes] = useState(false);

  const { token } = getAuthCredentials();

  const inventoryById = useMemo(
    () => new Map((ingredients || []).map((i) => [normalizeId(i._id), i])),
    [ingredients]
  );

  // per-unit price from inventory instances
  const perUnitPrice = (inv) => {
    const first = Array.isArray(inv?.instances) ? inv.instances[0] : undefined;
    if (first && Number(first.purchaseAmount)) {
      return Number(first.inventoryPrice) / Number(first.purchaseAmount);
    }
    return Number(inv?.pricePerUnit ?? 0);
  };

  const calculateTotalIngredientPrice = () =>
    selectedIngredients.reduce((total, ing) => {
      if (ing.__isSubRecipe) return total; // ignore meta rows
      const key = qtyKeyOf(ing);
      const inv = inventoryById.get(normalizeId(ing.inventoryId));
      const unit = perUnitPrice(inv);
      const qty = Number(ingredientQuantities[key] ?? ing.quantity ?? 1) || 1;
      return total + unit * qty;
    }, 0);

  // ---------- Sub-recipe helpers ----------
  const expandSubRecipeIngredients = (
    menuItem,
    parentUIKey,
    qtyMultiplier = 1,
    seen = new Set()
  ) => {
    const out = [];
    const mid = normalizeId(menuItem._id);
    if (!mid || seen.has(mid)) return out;
    const seenNext = new Set(seen);
    seenNext.add(mid);

    const items = Array.isArray(menuItem.menuIngredients)
      ? menuItem.menuIngredients
      : [];
    items.forEach((mi, idx) => {
      // nested sub-recipe?
      if (mi.subRecipeId) {
        const childId = normalizeId(mi.subRecipeId);
        const child = (menuItems || []).find(
          (mm) => normalizeId(mm._id) === childId
        );
        const amt = Number(mi.amount || 1) * qtyMultiplier;
        if (child) {
          out.push(
            ...expandSubRecipeIngredients(child, parentUIKey, amt, seenNext)
          );
        }
        return;
      }

      const invId = normalizeId(mi.ingredientId) || "";
      const inv = inventoryById.get(invId);
      if (!inv) return;

      const uiKey = `${parentUIKey}:ing:${invId}:${idx}`;
      out.push({
        ...inv,
        uiKey,
        inventoryId: invId,
        inventoryName: inv.inventoryName,
        unitOfMeasurement: inv.unitOfMeasurement,
        instances: inv.instances,
        parentUIKey,
        fromSubRecipeId: mid,
        fromSubRecipeName: menuItem.menuName,
        baseAmountFromSub: Number(mi.amount || 1) * qtyMultiplier,
      });
    });

    return out;
  };

  const handleAddSubRecipeItems = (pickedMenuItems) => {
    const nextSel = [...selectedIngredients];
    const nextQty = { ...ingredientQuantities };

    pickedMenuItems.forEach((m) => {
      const subId = normalizeId(m._id);
      const already = nextSel.some(
        (it) => it.__isSubRecipe && normalizeId(it.subRecipeId) === subId
      );
      if (already) return;

      // meta row
      const parentKey = `sub:${subId}`;
      nextSel.push({
        uiKey: parentKey,
        inventoryId: parentKey, // UI-only
        inventoryName: m.menuName,
        quantity: 1,
        __isSubRecipe: true,
        subRecipeId: subId,
        subRecipeName: m.menuName,
      });
      nextQty[parentKey] = 1;

      // expand into real rows
      const expanded = expandSubRecipeIngredients(m, parentKey, 1);
      expanded.forEach((row) => {
        nextSel.push(row);
        nextQty[qtyKeyOf(row)] = row.baseAmountFromSub;
      });
    });

    setSelectedIngredients(nextSel);
    setIngredientQuantities(nextQty);
    setShowSubRecipes(true);
  };

  const handleSubRecipeQuantityChange = (srMetaRow, newValue) => {
    const parentKey = qtyKeyOf(srMetaRow);
    const val = newValue === "" ? "" : Math.max(0, Number(newValue));
    setIngredientQuantities((prev) => {
      const copy = { ...prev, [parentKey]: val };
      selectedIngredients.forEach((it) => {
        if (!it.__isSubRecipe && it.parentUIKey === parentKey) {
          const base = Number(it.baseAmountFromSub || 0);
          copy[qtyKeyOf(it)] = val === "" ? "" : base * (val || 0);
        }
      });
      return copy;
    });
  };
  // ---------------------------------------

  // Load item into form (hydrate ingredients + rebuild sub-recipe meta rows)
  useEffect(() => {
    if (!item) return;

    setName(item.menuName);
    setTranslations(item.translations || {});
    setCode(item.code || item.menuName);
    setUniqueName(item.uniqueName || " ");
    setMenuPax(item.menuPax || 1);
    setProcessTime(item.processTime || 0);

    setCategory({
      categoryId: item.categoryId,
      categoryName:
        menuCategories.find((cat) => cat.categoryId === item.categoryId)
          ?.categoryName || "",
    });

    const mergedTx = DEFAULT_TX_TYPES.map((def) => {
      const found = (item.transactType || []).find(
        (t) => (t.type || "").toLowerCase() === def.type
      );
      return found ? { type: def.type, enabled: !!found.enabled } : def;
    });
    setTransactType(mergedTx);

    const nextSel = [];
    const nextQty = {};

    // First pass: create meta rows for each subRecipeId present
    const subIds = new Map(); // subId -> parentUIKey
    (Array.isArray(item.menuIngredients) ? item.menuIngredients : []).forEach(
      (mi) => {
        const sId = normalizeId(mi.subRecipeId);
        if (!sId) return;
        if (!subIds.has(sId)) {
          const foundSub = (menuItems || []).find(
            (m) => normalizeId(m._id) === sId
          );
          const parentKey = `sub:${sId}`;
          subIds.set(sId, parentKey);
          nextSel.push({
            uiKey: parentKey,
            inventoryId: parentKey,
            inventoryName:
              foundSub?.menuName || mi.subRecipeName || "Sub-Recipe",
            quantity: 1, // we can only infer 1 from flattened data
            __isSubRecipe: true,
            subRecipeId: sId,
            subRecipeName:
              foundSub?.menuName || mi.subRecipeName || "Sub-Recipe",
          });
          nextQty[parentKey] = 1;
        }
      }
    );

    // Second pass: push regular ingredient rows (attach to meta if subRecipeId exists)
    (Array.isArray(item.menuIngredients) ? item.menuIngredients : []).forEach(
      (mi, idx) => {
        const invId =
          normalizeId(mi.ingredientId) ||
          normalizeId(mi.inventoryId) ||
          normalizeId(
            (ingredients || []).find(
              (ing) =>
                (ing.inventoryName || "").toLowerCase().trim() ===
                (mi.ingredientName || "").toLowerCase().trim()
            )?._id
          );

        if (!isValidHexId(invId)) {
          console.warn(
            "Skipping menuIngredient without valid inventoryId:",
            mi
          );
          return;
        }

        const inv = inventoryById.get(invId);
        const parentUIKey = mi.subRecipeId
          ? subIds.get(normalizeId(mi.subRecipeId))
          : undefined;

        const row = {
          ...inv,
          uiKey: parentUIKey ? `${parentUIKey}:ing:${invId}:${idx}` : invId,
          inventoryId: invId,
          inventoryName:
            inv?.inventoryName || mi.ingredientName || "Ingredient",
          unitOfMeasurement: inv?.unitOfMeasurement || "UOM",
          quantity: Number(mi.amount || 1) || 1,
          parentUIKey,
          ...(parentUIKey
            ? {
                fromSubRecipeId: normalizeId(mi.subRecipeId),
                fromSubRecipeName:
                  (menuItems || []).find(
                    (m) => normalizeId(m._id) === normalizeId(mi.subRecipeId)
                  )?.menuName || mi.subRecipeName,
                baseAmountFromSub: Number(mi.amount || 1), // meta qty assumed 1
              }
            : {}),
        };

        nextSel.push(row);
        nextQty[qtyKeyOf(row)] = Number(mi.amount || 1) || 1;
      }
    );

    setSelectedIngredients(nextSel);
    setIngredientQuantities(nextQty);
    setShowSubRecipes(subIds.size > 0);

    setPrice(item.basePrice ?? item.price ?? "");
    setDescription(item.description || "");
    setAddons(item.addOns || []);
    setTags(item.menuTags?.join(", ") || "");

    // Preview image
    const loadPreview = async () => {
      if (item.imageId) {
        const blob = await getImage(item.imageId);
        if (blob) {
          setImage(URL.createObjectURL(blob));
          return;
        }
      }
      if (item.menuImage && typeof item.menuImage === "string") {
        const url = item.menuImage.startsWith("http")
          ? item.menuImage
          : `${process.env.REACT_APP_BACKEND_URL}/${item.menuImage}`;
        setImage(url);
      }
    };
    loadPreview();
  }, [item, menuCategories, inventoryById, ingredients, menuItems]);

  // duplicate name guard
  useEffect(() => {
    const isDuplicate = (menuItems || []).some(
      (m) =>
        (m.menuName || "").toLowerCase().trim() ===
          (name || "").toLowerCase().trim() && m._id !== item?._id
    );
    setNameError(
      isDuplicate ? "A menu item with this name already exists." : ""
    );
  }, [name, menuItems, item?._id]);

  // image handlers
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    const allowed = ["image/jpeg", "image/png", "image/gif"];
    const maxSize = 5 * 1024 * 1024;
    if (!file) return;
    if (!allowed.includes(file.type)) {
      Swal.fire(
        "Unsupported file type",
        "Only JPEG, PNG, and GIF are allowed.",
        "error"
      );
      return;
    }
    if (file.size > maxSize) {
      Swal.fire("File too large", "File size exceeds 5 MB.", "error");
      return;
    }
    setCropSrc(URL.createObjectURL(file));
    setCropperOpen(true);
  };

  const handleCropSave = (blob) => {
    const previewUrl = URL.createObjectURL(blob);
    setImage(previewUrl);
    setImageFile(new File([blob], "menu-image.jpg", { type: blob.type }));
    setCropperOpen(false);
  };

  const handleEditImage = async () => {
    if (!image) return;
    if (image.startsWith("blob:") || image.startsWith("data:")) {
      setCropSrc(image);
      setCropperOpen(true);
      return;
    }
    try {
      const resp = await fetch(image, { mode: "cors" });
      const blob = await resp.blob();
      const objURL = URL.createObjectURL(blob);
      setCropSrc(objURL);
      setCropperOpen(true);
    } catch (err) {
      console.error("Unable to prepare image for cropping:", err);
    }
  };

  // category deletion
  const handleDeleteCategory = async (categoryId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/delete-category`,
        { headers: { Authorization: `Bearer ${token}` }, data: { categoryId } }
      );
      fetchMenuCategories();
    } catch (error) {
      console.error("Error deleting category:", error);
    }
  };

  // add/remove ingredients (top-level regular ingredients)
  const handleAddIngredient = (ingredient) => {
    const invId = normalizeId(ingredient._id);
    if (!isValidHexId(invId)) return;
    const already = selectedIngredients.some(
      (it) =>
        !it.__isSubRecipe &&
        !it.parentUIKey &&
        normalizeId(it.inventoryId) === invId
    );
    if (already) return;

    setSelectedIngredients((prev) => [
      ...prev,
      {
        inventoryId: invId,
        inventoryName: ingredient.inventoryName,
        unitOfMeasurement: ingredient.unitOfMeasurement || "UOM",
        quantity: 1,
      },
    ]);
    setIngredientQuantities((prev) => ({ ...prev, [invId]: 1 }));
  };

  const handleRemoveIngredient = (ingredient) => {
    // removing a sub-recipe meta row removes its children
    if (ingredient.__isSubRecipe) {
      const parentKey = qtyKeyOf(ingredient);
      const nextSel = selectedIngredients.filter(
        (it) => qtyKeyOf(it) !== parentKey && it.parentUIKey !== parentKey
      );
      const nextQty = { ...ingredientQuantities };
      delete nextQty[parentKey];
      selectedIngredients.forEach((it) => {
        if (it.parentUIKey === parentKey) delete nextQty[qtyKeyOf(it)];
      });
      setSelectedIngredients(nextSel);
      setIngredientQuantities(nextQty);
      return;
    }

    const key = qtyKeyOf(ingredient);
    setSelectedIngredients((prev) => prev.filter((it) => qtyKeyOf(it) !== key));
    setIngredientQuantities((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const handleQuantityChange = (keyOrId, value) => {
    const parsed = parseFloat(value);
    setIngredientQuantities((prev) => ({
      ...prev,
      [keyOrId]: isNaN(parsed) ? 1 : parsed,
    }));
  };

  // Build a safe menuIngredients payload (merge by invId + subRecipeId)
  const regularIngredients = useMemo(
    () => selectedIngredients.filter((i) => !i.__isSubRecipe),
    [selectedIngredients]
  );
  const subRecipeItems = useMemo(
    () => selectedIngredients.filter((i) => i.__isSubRecipe),
    [selectedIngredients]
  );

  const buildMenuIngredientsPayload = () => {
    const map = new Map(); // key: invId::subId
    regularIngredients.forEach((row) => {
      const invId = normalizeId(row.inventoryId);
      if (!isValidHexId(invId)) return;

      const qKey = qtyKeyOf(row);
      const qty = Number(ingredientQuantities[qKey] ?? row.quantity ?? 1) || 1;

      const inv = inventoryById.get(invId);
      const ppu = perUnitPrice(inv);

      const subId = row.fromSubRecipeId
        ? normalizeId(row.fromSubRecipeId)
        : "none";
      const key = `${invId}::${subId}`;

      const base = {
        ingredientId: invId,
        ingredientName: inv?.inventoryName || row.inventoryName || "Ingredient",
        amount: qty,
        ingredientPrice: +(ppu * qty).toFixed(4),
        ...(row.fromSubRecipeId
          ? {
              subRecipeId: normalizeId(row.fromSubRecipeId),
              subRecipeName: row.fromSubRecipeName,
            }
          : {}),
      };

      const existing = map.get(key);
      if (!existing) map.set(key, base);
      else {
        existing.amount += qty;
        existing.ingredientPrice = +(
          existing.ingredientPrice +
          ppu * qty
        ).toFixed(4);
      }
    });

    return Array.from(map.values());
  };

  const isFormValid =
    name &&
    !nameError &&
    category &&
    regularIngredients.length > 0 &&
    (price || price === 0);

  const handleSave = async () => {
    const formData = new FormData();
    if (imageFile) formData.append("menuImage", imageFile);

    const payloadMenuIngredients = buildMenuIngredientsPayload();

    const jsonPayload = {
      menuName: name,
      translations,
      code: code || name,
      uniqueName,
      categoryId: category.categoryId,
      categoryName: category.categoryName,
      basePrice: Number(price) || calculateTotalIngredientPrice(),
      menuPax,
      description,
      addOns: addons,
      menuTags: tags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      transactType,
      menuIngredients: payloadMenuIngredients, // includes subRecipeId/name when applicable
      processTime: parseInt(processTime, 10),
    };

    formData.append("data", JSON.stringify(jsonPayload));

    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/v1/api/menu/edit/${item._id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      fetchMenuData();
      onClose();
      setTimeout(() => {
        Swal.fire({
          icon: "success",
          title: "Updated",
          text: "Menu item updated successfully.",
        });
      }, 100);
    } catch (error) {
      console.error("Error updating menu item:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update the menu item. Please try again.",
      });
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth>
        <DialogTitle>Edit Menu Item</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!nameError}
            helperText={nameError}
          />

          <Button
            fullWidth
            variant="outlined"
            sx={{ mt: 1, mb: 1 }}
            onClick={() => setShowTranslations(!showTranslations)}
          >
            {showTranslations ? "Hide Translations" : "Show Translations"}
          </Button>

          {showTranslations && (
            <>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mb: 1, lineHeight: 1.2 }}
              >
                Auto Translate uses Google Translate. Optional and use
                cautiously.
              </Typography>
              {["en", "tl", "zh", "th"].map((lg) => (
                <Grid
                  key={lg}
                  item
                  xs={12}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <TextField
                    label={
                      {
                        en: "English",
                        tl: "Tagalog",
                        zh: "Chinese",
                        th: "Thai",
                      }[lg]
                    }
                    fullWidth
                    margin="normal"
                    value={translations[lg] || ""}
                    onChange={(e) =>
                      setTranslations((prev) => ({
                        ...prev,
                        [lg]: e.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: Boolean(translations[lg]) }}
                  />
                  <Tooltip
                    title={`Translate to ${
                      {
                        en: "English",
                        tl: "Tagalog",
                        zh: "Chinese",
                        th: "Thai",
                      }[lg]
                    }`}
                  >
                    <span>
                      <IconButton
                        onClick={() => {
                          setLoadingTranslation((p) => ({ ...p, [lg]: true }));
                          translateText(name, lg)
                            .then((txt) => {
                              setTranslations((prev) => ({
                                ...prev,
                                [lg]: txt,
                              }));
                            })
                            .catch(console.error)
                            .finally(() => {
                              setLoadingTranslation((p) => ({
                                ...p,
                                [lg]: false,
                              }));
                            });
                        }}
                        disabled={!name || loadingTranslation[lg]}
                      >
                        {loadingTranslation[lg] ? (
                          <CircularProgress size={24} />
                        ) : (
                          <GTranslateIcon />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                </Grid>
              ))}
            </>
          )}

          <TextField
            label="Code (Optional)"
            fullWidth
            margin="normal"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            helperText="Leave empty to use the name as the code."
          />
          <TextField
            label="Unique Name (Optional)"
            fullWidth
            margin="normal"
            value={uniqueName}
            onChange={(e) => setUniqueName(e.target.value)}
          />

          <Autocomplete
            options={[
              ...menuCategories,
              { categoryId: "add-new", categoryName: "Add New Category" },
            ]}
            getOptionLabel={(option) => option.categoryName || ""}
            value={category}
            onChange={(event, newValue) => {
              if (newValue && newValue.categoryId === "add-new") {
                setIsAddingCategory(true);
              } else {
                setCategory(newValue);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Category"
                variant="outlined"
                margin="normal"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.categoryId}>
                <span style={{ flexGrow: 1 }}>{option.categoryName}</span>
                {option.categoryId !== "add-new" && (
                  <Tooltip title="Delete category">
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteCategory(option.categoryId);
                      }}
                      color="error"
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </li>
            )}
          />

          {isAddingCategory && (
            <AddMenuCategoryDialog
              open={isAddingCategory}
              onClose={() => setIsAddingCategory(false)}
              onCategoryAdded={(newCategory) => {
                setMenuCategories((prev) => [...prev, newCategory]);
                setCategory(newCategory);
                setIsAddingCategory(false);
              }}
            />
          )}

          <FormControl fullWidth margin="normal">
            <InputLabel id="tx-type-label">Transaction Types</InputLabel>
            <Select
              labelId="tx-type-label"
              label="Transaction Types"
              multiple
              value={transactType.filter((t) => t.enabled).map((t) => t.type)}
              onChange={(e) => {
                const selected = e.target.value;
                setTransactType((prev) =>
                  prev.map((t) => ({
                    ...t,
                    enabled: selected.includes(t.type),
                  }))
                );
              }}
              renderValue={(selected) =>
                selected.length ? selected.join(", ") : "None"
              }
            >
              {transactType.map((t) => (
                <MenuItem key={t.type} value={t.type}>
                  <Checkbox checked={t.enabled} />
                  <ListItemText
                    primary={t.type
                      .split("-")
                      .map((s) => s[0].toUpperCase() + s.slice(1))
                      .join(" ")}
                    secondary={t.enabled ? "Enabled" : "Disabled"}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Grid container spacing={2} alignItems="center">
            <Grid item>
              <Button
                variant="contained"
                component="label"
                startIcon={<PhotoCamera />}
              >
                Upload Image
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={handleImageUpload}
                />
              </Button>
            </Grid>

            {image && (
              <>
                <Grid item>
                  <Button variant="outlined" onClick={handleEditImage}>
                    Edit Image
                  </Button>
                </Grid>
                <Grid item>
                  <img
                    src={image}
                    alt="Preview"
                    style={{ width: 100, height: 100, objectFit: "cover" }}
                  />
                </Grid>
              </>
            )}
          </Grid>

          <TextField
            label="Preparation Time (in minutes)"
            fullWidth
            margin="normal"
            type="number"
            value={processTime}
            onChange={(e) => setProcessTime(e.target.value)}
            helperText="Enter the estimated preparation time for this menu item"
          />

          {/* INGREDIENTS (top-level regular) */}
          <Typography variant="subtitle2" sx={{ mt: 2 }}>
            Ingredients
          </Typography>
          <Autocomplete
            options={ingredients}
            getOptionLabel={(option) =>
              `${option.inventoryName} - ${option.unitOfMeasurement || ""}`
            }
            onChange={(event, newValue) => {
              if (newValue) handleAddIngredient(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add New Ingredients"
                margin="normal"
                variant="outlined"
              />
            )}
          />

          <List>
            {regularIngredients.map((ingredient) => {
              const qKey = qtyKeyOf(ingredient);
              const inv = inventoryById.get(
                normalizeId(ingredient.inventoryId)
              );
              return (
                <ListItem key={qKey}>
                  <ListItemText
                    primary={`${ingredient.inventoryName} (${
                      inv?.unitOfMeasurement ||
                      ingredient.unitOfMeasurement ||
                      "UOM"
                    })`}
                  />
                  <TextField
                    label="Quantity"
                    type="number"
                    inputProps={{ min: 0, step: 0.01 }}
                    value={
                      ingredientQuantities[qKey] ?? ingredient.quantity ?? ""
                    }
                    onChange={(e) => {
                      const v =
                        e.target.value === ""
                          ? ""
                          : Math.max(0, Number(e.target.value));
                      handleQuantityChange(qKey, v);
                    }}
                    size="small"
                    sx={{ width: 80, mr: 1 }}
                  />
                  <IconButton
                    edge="end"
                    onClick={() => handleRemoveIngredient(ingredient)}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              );
            })}
          </List>

          {/* SUB-RECIPES */}
          <Button
            fullWidth
            variant="outlined"
            sx={{ mt: 2, mb: 1 }}
            onClick={() => setShowSubRecipes((s) => !s)}
          >
            {showSubRecipes ? "Hide Sub-Recipes" : "Show Sub-Recipes"}
          </Button>

          <Collapse in={showSubRecipes} timeout="auto" unmountOnExit>
            <List dense sx={{ mt: 1 }}>
              {subRecipeItems.length === 0 ? (
                <ListItem>
                  <ListItemText primary="No sub-recipes added." />
                </ListItem>
              ) : (
                subRecipeItems.map((sr) => {
                  const qKey = qtyKeyOf(sr);
                  return (
                    <ListItem key={qKey}>
                      <ListItemText
                        primary={sr.subRecipeName || sr.inventoryName}
                        secondary="Sub-Recipe"
                      />
                      <TextField
                        label="Quantity"
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        value={ingredientQuantities[qKey] ?? sr.quantity ?? ""}
                        onChange={(e) => {
                          const v =
                            e.target.value === ""
                              ? ""
                              : Math.max(0, Number(e.target.value));
                          handleSubRecipeQuantityChange(sr, v);
                        }}
                        size="small"
                        sx={{ width: 80, mr: 1 }}
                      />
                      <IconButton
                        edge="end"
                        onClick={() => handleRemoveIngredient(sr)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItem>
                  );
                })
              )}
              <ListItem>
                <Button
                  variant="outlined"
                  onClick={() => setSubRecipeOpen(true)}
                >
                  Add Sub-Recipe
                </Button>
              </ListItem>
            </List>
          </Collapse>

          <TextField
            label="Base Price"
            fullWidth
            margin="normal"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            helperText={`Total Price of ingredients: ${calculateTotalIngredientPrice()}`}
          />

          <TextField
            label="No. of Pax"
            fullWidth
            margin="normal"
            type="number"
            value={menuPax}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              setMenuPax(v < 1 ? 1 : v);
            }}
            helperText="Number of people this item is meant to serve (minimum 1)"
          />

          <TextField
            label="Description"
            fullWidth
            margin="normal"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />

          <List>
            {addons.map((addonId, index) => {
              const addId = normalizeId(addonId);
              const addon = (menuItems || []).find(
                (m) => normalizeId(m._id) === addId
              );
              return (
                <ListItem key={addId || index}>
                  <ListItemText primary={addon?.menuName || "Unknown Addon"} />
                  <IconButton
                    edge="end"
                    onClick={() =>
                      setAddons(
                        addons.filter((id) => normalizeId(id) !== addId)
                      )
                    }
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItem>
              );
            })}
          </List>

          <TextField
            label="Tags"
            fullWidth
            margin="normal"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            helperText="Separate tags with commas"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary" disabled={!isFormValid}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <HandleCropMenuImage
        open={cropperOpen}
        imageSrc={cropSrc}
        onCancel={() => setCropperOpen(false)}
        onSave={handleCropSave}
      />

      <SubRecipeDialog
        open={subRecipeOpen}
        onClose={() => setSubRecipeOpen(false)}
        menuItems={menuItems}
        excludeIds={[normalizeId(item?._id)].filter(Boolean)}
        onAdd={handleAddSubRecipeItems}
        allowMultiple={true}
      />
    </>
  );
};

export default EditMenuItemDialog;
